import React, { useState, useEffect, useRef } from 'react';
import { useTheme, createThemedStyles } from '../../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faCheckCircle } from '@/icons';
import type { BaseCalculatorProps } from './types';

export const DeductionCalculator: React.FC<BaseCalculatorProps> = ({
    currentValue,
    decimalPlaces,
    onSave,
    onClose,
}) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const grossAmountRef = useRef<HTMLInputElement>(null);
    const [grossAmount, setGrossAmount] = useState('');
    const [payPeriod, setPayPeriod] = useState<'weekly' | 'biweekly' | 'semimonthly' | 'monthly'>('weekly');
    const [annualIncome, setAnnualIncome] = useState(0);

    useEffect(() => {
        if (currentValue && !isNaN(parseFloat(currentValue)) && parseFloat(currentValue) > 0) {
            const value = parseFloat(currentValue);
            if (value < 5000) {
                setPayPeriod('weekly');
                setGrossAmount((value / 52).toFixed(2));
            } else if (value < 10000) {
                setPayPeriod('biweekly');
                setGrossAmount((value / 26).toFixed(2));
            } else if (value < 20000) {
                setPayPeriod('semimonthly');
                setGrossAmount((value / 24).toFixed(2));
            } else {
                setPayPeriod('monthly');
                setGrossAmount((value / 12).toFixed(2));
            }
        } else {
            setGrossAmount('');
        }
    }, [currentValue]);

    useEffect(() => {
        grossAmountRef.current?.focus();
    }, []);

    useEffect(() => {
        const amount = parseFloat(grossAmount) || 0;
        const multiplier = payPeriod === 'weekly' ? 52 : payPeriod === 'biweekly' ? 26 : payPeriod === 'semimonthly' ? 24 : 12;
        setAnnualIncome(amount * multiplier);
    }, [grossAmount, payPeriod]);

    const handleSave = () => {
        const rounded = Math.round(annualIncome * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
        onSave(rounded);
        onClose();
    };

    return (
        <>
            <div style={{
                backgroundColor: '#dbeafe',
                border: '1px solid #93c5fd',
                borderRadius: theme.borderRadius.sm,
                padding: theme.spacing.md,
                marginBottom: theme.spacing.lg,
                display: 'flex',
                alignItems: 'flex-start',
                gap: theme.spacing.sm,
            }}>
                <i className="fas fa-info-circle" style={{ color: '#1e40af', fontSize: theme.typography.fontSize.md, marginTop: '2px' }} />
                <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: '#1e40af', lineHeight: 1.5 }}>
                    Enter the per pay period amount and Calculist will calculate the annual amount for you and update the deduction value accordingly.
                </p>
            </div>
            <div style={{ marginBottom: theme.spacing.lg }}>
                <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>Deduction Amount per Check:</label>
                <input
                    ref={grossAmountRef}
                    type="number"
                    value={grossAmount}
                    onChange={(e) => setGrossAmount(e.target.value)}
                    style={{ width: '100%', padding: theme.spacing.sm, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.sm, fontSize: theme.typography.fontSize.sm }}
                    placeholder="0.00"
                    step="0.01"
                />
            </div>
            <div style={{ marginBottom: theme.spacing.lg }}>
                <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>Select Applicable Pay Period:</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.sm }}>
                    {[
                        { value: 'weekly' as const, label: 'Weekly' },
                        { value: 'biweekly' as const, label: 'Every 2 Weeks' },
                        { value: 'semimonthly' as const, label: 'Twice Monthly' },
                        { value: 'monthly' as const, label: 'Monthly' },
                    ].map((opt) => {
                        const isSelected = payPeriod === opt.value;
                        return (
                            <button
                                key={opt.value}
                                onClick={() => setPayPeriod(opt.value)}
                                style={{
                                    padding: theme.spacing.sm,
                                    border: `2px solid ${theme.colors.primary}`,
                                    borderRadius: theme.borderRadius.sm,
                                    fontSize: theme.typography.fontSize.sm,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    cursor: 'pointer',
                                    backgroundColor: theme.colors.primary,
                                    color: theme.colors.text.inverse,
                                    opacity: isSelected ? 1 : 0.8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: theme.spacing.sm,
                                }}
                            >
                                {opt.label}
                                {isSelected && <FontAwesomeIcon icon={faCheckCircle} style={{ color: theme.colors.status.success }} />}
                            </button>
                        );
                    })}
                </div>
            </div>
            <div style={{ marginBottom: theme.spacing.xl }}>
                <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>Annual Deduction:</label>
                <div style={{ padding: theme.spacing.sm, backgroundColor: theme.colors.surfaceSecondary, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.sm, fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.primary }}>
                    ${annualIncome.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
                <button onClick={onClose} style={{ ...themedStyles.button.primary, backgroundColor: 'transparent', color: theme.colors.text.primary, border: `1px solid ${theme.colors.border.default}`, padding: `${theme.spacing.sm} ${theme.spacing.lg}`, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button onClick={handleSave} style={{ ...themedStyles.button.primary, backgroundColor: theme.colors.primary, color: theme.colors.text.inverse, padding: `${theme.spacing.sm} ${theme.spacing.lg}`, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <FontAwesomeIcon icon={faCheck} /> Apply
                </button>
            </div>
        </>
    );
};
